import {action, computed, decorate, observable, reaction} from "mobx";
import _ from "lodash";
import dayjs from "dayjs";
import {toast} from "react-toastify";

class GameMetadataStore {
  constructor(statsApi, sportsReferenceStore) {
    this.statsApi = statsApi;
    this.sportsReferenceStore = sportsReferenceStore;

    this.reset();
    this.loadGames();

    reaction(
        () => this.sportId,
        () => this.loadGames()
    );

    reaction(
        () => this.date,
        () => this.loadGames()
    );

    reaction(
      () => this.sportsReferenceStore.sports.length,
      (length) => {
        if (length > 0) {
          this.sportId = this.sportsReferenceStore.defaultSport;
        }
      }
    );
  }

  setDate(date) {
    if (date !== this.date) {
      this.games = [];
    }

    this.date = date;
  }

  setSportId(sportId) {
    if (sportId !== this.sportId) {
      this.games = [];
    }

    this.sportId = sportId;
  }


  loadGames() {
    const scheduleDate = dayjs(this.date.toLocaleString()).format("YYYY-MM-DD");

    this.statsApi
    .getSchedule(scheduleDate, this.sportId)
    .then(games => {
      // Get all the scheduled games for this day
      return games.dates.length > 0 ? games.dates[0].games : [];
    })
    .then(games => {
      // Create a human friendly description for each game
      games.forEach(
          g =>
              (g[
                  "description"
                  ] = `${g.gamePk} - ${g.teams.away.team.abbreviation} @ ${g.teams.home.team.abbreviation}`)
      );
      this.games = games;
      return games;
    })
    .then(games => {
      // Load up any other events we care about for this sport on that date (All-Star Weekend)
      this.statsApi
      .getEventSchedule(scheduleDate, this.sportId)
      .then(eventSchedule => {
        let events =
            eventSchedule.dates.length > 0
                ? eventSchedule.dates[0].events
                : [];
        events = events
        .filter(e => e.eventType.code === "A")
        .map(e => {
          return {
            gamePk: e["id"],
            description: e["name"]
          };
        });
        this.games = games.concat(events);
      });
    });
  }

  loadGameDetails(gamePk) {
    return this.statsApi.getAtBats(gamePk).then(data => {
      return data["allPlays"]
      .filter(atBat => atBat["playEvents"].length > 0)
      .map(atBat => {
        return {
          startTime: atBat["playEvents"][atBat["playEvents"].length - 1]["startTime"],
          endTime: atBat["playEvents"][atBat["playEvents"].length - 1]["endTime"],
          isScoringPlay: atBat["about"]["isScoringPlay"],
          inning: atBat["about"]["inning"],
          isTopInning: atBat["about"]["isTopInning"],
          atBatIndex: atBat["about"]["atBatIndex"],
          description: atBat["result"]["description"]
        };
      });
    });
  }

  async setGamePk(gamePk) {
    if (this.selectedGame && this.selectedGame.gamePk === gamePk) {
      return;
    }

    this.descriptionFilter = "";
    let existingGame = _.find(this.games, g => g.gamePk === gamePk);

    // gamePk must've been passed manually. Look up this game pk from stats api and load other games on that date
    if (!existingGame) {
      const game = await this.statsApi.getGame(gamePk);
      existingGame = game.dates.length > 0 ? game.dates[0].games[0] : null;
      // If we loaded a new game reset the filters to match
      if (existingGame) {
        const gameSportId = existingGame["teams"]["home"]["team"]["sport"]["id"]
        const userAllowedSports = this.sportsReferenceStore.sports.map(s => s.id);

        if (!userAllowedSports.includes(gameSportId)) {
          toast.error("You are not entitled to this content. If you feel this is in error, please contact Sportybot support.", {
            position: "top-right",
            autoClose: 6000
          });
          return;
        }

        this.sportId = gameSportId;
        const [year, month, day] = existingGame["officialDate"].split("-");
        this.date = new Date(year, month - 1, day);
      } else {
        // Maybe the game pk passed in was a tracking event (home run derby)
        const events = await this.statsApi.getEvent(gamePk);
        const event =
            events.dates.length > 0 ? events.dates[0].events[0] : null;
        if (event) {
          existingGame = {
            gamePk: event["id"],
            description: event["name"]
          };
          const [year, month, day] = events.dates[0].date.split("-");
          this.date = new Date(year, month - 1, day);
        }
      }
    }

    if (existingGame) {
      existingGame["atBats"] = await this.loadGameDetails(gamePk);
      this.selectedGame = existingGame;
    }
  }

  reset() {
    this.games = [];
    this.selectedGame = null;
    this.date = new Date();
    this.sportId = this.sportsReferenceStore.defaultSport;
    this.atBats = [];
    this.descriptionFilter = "";
  }

  get filteredAtBats() {
    if (!this.selectedGame) {
      return [];
    }

    if (!this.descriptionFilter || this.descriptionFilter === "") {
      return this.selectedGame.atBats;
    }

    const descriptionFilterLower = this.descriptionFilter.toLowerCase();
    return this.selectedGame.atBats.filter(
        ab =>
            ab.description &&
            ab.description.toLowerCase().includes(descriptionFilterLower)
    );
  }
}

decorate(GameMetadataStore, {
  selectedGame: observable,
  atBats: observable,
  descriptionFilter: observable,
  filteredAtBats: computed,
  date: observable,
  sportId: observable,
  games: observable,
  reset: action,
  setDate: action,
  setGamePk: action,
  setSportId: action
});

export default GameMetadataStore;



// WEBPACK FOOTER //
// ./src/stores/customClips/GameMetadataStore.js